import RefundDialog from '../components/order/RefundDialog';
import { Notice } from '@verlo/shared/components/ui/notice';
import { useOrigin } from '../components/BackBar';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tablesApi, menuApi, modifiersApi, ordersApi, paymentsApi, reservationsApi, kitchenApi, promotionsApi } from '@verlo/shared/services/api';
import { randomIdempotencyKey } from '@verlo/shared/lib/idempotency';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { toast } from 'sonner';
import { saveOrderDraft, loadOrderDraft, clearOrderDraft, moveOrderDraft } from '../lib/orderDraft';
import { addOrMore, adoptLines, anotherOf, changeUnsent, sameDish, voidLines, toPayloadLine } from '../lib/sentLines';
import { clashingLines, withAllergyNotes, withoutLines } from '../lib/allergyCheck';
import { setWhere, clearWhere } from '../lib/lockResume';
import { courseOf } from '../lib/courses';
import AllergyClashDialog from '../components/order/AllergyClashDialog';
import MoveTableDialog from '../components/tables/MoveTableDialog';
import AllergyPicker from '../components/tables/AllergyPicker';
import OccasionPicker from '../components/tables/OccasionPicker';
import { Button } from '@verlo/shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@verlo/shared/components/ui/dialog';

// Import refactored components
import {
  TableOrderHeader,
  MenuSection,
  OrderPanel,
  SizeSelectDialog,
  ModifierSelectDialog,
  ItemDetailDialog,
  StatusChangeDialog,
  ManagerOverrideDialog,
  VoidConfirmDialog,
  ManagerActionDialog,
  MessageDialog,
  OffersDialog,
  ExtendTimeDialog,
  EditCoversDialog,
  ItemEditDialog,
  PaymentDialog,
} from '../components/order';
import ModifierSheet, { ticketWords } from '../components/order/ModifierSheet';
import { sheetInitialFor } from '../lib/sheetEdit';
import { asksOnTap } from '@verlo/shared/lib/tapChoice';
import { duration } from '@verlo/shared/lib/duration';
import useVatRate, { vatInside } from '../hooks/useVatRate';
import { orderRef } from '@verlo/shared/lib/orderRef';
import { sizedName } from '@verlo/shared/lib/sizeChoice';

// Mirrors backend `_line_key` in routes/orders.py so the client flags the same
// reductions the server will — menu item + name + modifier set + notes, and a meal's own
// parts: two kids meals with different mains are two different things to cook.
const lineKey = (it) => JSON.stringify([
  it.menu_item_id,
  it.name,
  [...ticketWords(it.modifiers || [])].sort(),
  it.notes || '',
  (it.components || []).map((c) => ((c.quantity || 1) > 1 ? `${c.quantity}× ${c.name}` : c.name)),
]);

// True if `newItems` removes or reduces the quantity of any line in `oldItems`.
const reducesExistingItems = (oldItems, newItems) => {
  const oldQ = {};
  const newQ = {};
  (oldItems || []).forEach((it) => {
    const k = lineKey(it);
    oldQ[k] = (oldQ[k] || 0) + (Number(it.quantity) || 1);
  });
  (newItems || []).forEach((it) => {
    const k = lineKey(it);
    newQ[k] = (newQ[k] || 0) + (Number(it.quantity) || 1);
  });
  return Object.entries(oldQ).some(([k, was]) => (newQ[k] || 0) < was);
};

// What's still owed on an order, after whatever's already been paid. Every call site that
// re-derives the balance from an order response (after an items update, a void, a manager
// override) used to set it to `order.total` outright — correct for a fresh order, wrong for
// a part-paid one, where it re-asked for money already taken and the payment dialog then
// rejected the real remaining amount as "exceeds the balance due".
const balanceOf = (order) => Math.max(0, (order?.total || 0) - (order?.amount_paid || 0));

const TableOrderPage = () => {
  const { tableId, orderId } = useParams();
  // Where Back (and every "done" action) returns to: the screen that sent us, else the
  // ledger for an order opened by id, else the floor.
  const from = useOrigin(orderId ? { path: '/orders', label: 'Orders' } : { path: '/tables', label: 'Tables' });
  const goHome = () => navigate(from.path);
  // An order opened from the ledger that is already settled is a record, not a draft: no
  // menu, no quantity controls, no Send. (Its lines have no ticket behind them, so the pad
  // used to show them as "3 new" with a Send button — on a paid bill.)
  const SETTLED = ['paid', 'refunded', 'partial_refund', 'cancelled', 'completed'];
  const [refundOpen, setRefundOpen] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // Core state
  const [table, setTable] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [globalModifiers, setGlobalModifiers] = useState([]);
  const [order, setOrder] = useState(null);
  const [cart, setCart] = useState([]);
  // The modifier sheet (modifiers plan, 21 Sep): long press, always-ask, or editing a line.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetItem, setSheetItem] = useState(null);
  const [sheetInitial, setSheetInitial] = useState(null);
  const [sheetEditIndex, setSheetEditIndex] = useState(null);
  const openSheet = (item, initial = null, editIndex = null) => { setSheetItem(item); setSheetInitial(initial); setSheetEditIndex(editIndex); setSheetOpen(true); };
  // The order's kitchen/bar tickets: where each sent line is (sent / cooking / ready / served).
  const [tickets, setTickets] = useState([]);
  // Index of the line a tap just added, so the panel can bump it into view.
  const [lastAdded, setLastAdded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Payment state
  const [orderPayments, setOrderPayments] = useState([]);
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState('waiting');
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' | 'cash'
  const [splitBillMode, setSplitBillMode] = useState(false);
  const [splitType, setSplitType] = useState('amount');
  const [splitPeople, setSplitPeople] = useState(2);
  const [customAmount, setCustomAmount] = useState('');
  const [tipAmount, setTipAmount] = useState(0);
  // One idempotency key per payment attempt — reset each time the flow returns to 'waiting'.
  const paymentKeyRef = useRef(null);
  const goToPaymentStep = (step) => {
    if (step === 'waiting') paymentKeyRef.current = null;
    setPaymentStep(step);
  };
  const openCheckout = () => {
    paymentKeyRef.current = null;
    setPaymentStep('waiting');
    setSplitBillMode(false);
    setCheckoutOpen(true);
  };
  
  // Dialog state
  const [sizeSelectOpen, setSizeSelectOpen] = useState(false);
  const [itemForSize, setItemForSize] = useState(null);
  const [modifierSelectOpen, setModifierSelectOpen] = useState(false);
  const [itemForModifier, setItemForModifier] = useState(null);
  const [itemDetailOpen, setItemDetailOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editCoversDialogOpen, setEditCoversDialogOpen] = useState(false);
  const [newCovers, setNewCovers] = useState(1);
  const [editTimeOpen, setEditTimeOpen] = useState(false);
  // Allergies: the send check's hits while it is open, and the order screen's own editor.
  const [clashHits, setClashHits] = useState(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [allergyEdit, setAllergyEdit] = useState(null);   // { allergens, notes } while open
  const [occasionEdit, setOccasionEdit] = useState(null); // { occasion, notes } while open
  const [additionalTime, setAdditionalTime] = useState(30);
  
  // Item Edit state
  const [itemEditDialogOpen, setItemEditDialogOpen] = useState(false);
  const [editingCartIndex, setEditingCartIndex] = useState(null);
  const [itemModifiers, setItemModifiers] = useState([]);
  const [itemNotes, setItemNotes] = useState('');
  const [itemAllergyNotes, setItemAllergyNotes] = useState('');
  const [customModifier, setCustomModifier] = useState('');
  
  // Status change state
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [managerOverrideOpen, setManagerOverrideOpen] = useState(false);

  // Void step-up: a server trimming an already-sent order needs a manager PIN.
  const [voidPromptOpen, setVoidPromptOpen] = useState(false);
  // `{ items, label }` — the order's lines with the void applied, and what is being voided.
  const [pendingVoid, setPendingVoid] = useState(null);
  // A manager voiding holds `epos.void` and needs no PIN, only a moment to say "yes, that one".
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);

  // Item 9 — comps, discounts, price overrides. `managerAction` holds what the
  // dialog is acting on; `undoAction` is the PIN prompt for taking one back off
  // when the operator doesn't hold the permission themselves.
  const [managerAction, setManagerAction] = useState(null);
  const [undoAction, setUndoAction] = useState(null);
  // Promotions: the bill-level offers a server can tap right now (the Offers row).
  const [tapOffers, setTapOffers] = useState([]);
  const [offersOpen, setOffersOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  // Messages typed before the order exists. "Mains together" is usually said while the
  // order is still being taken, so they wait here and travel on the create — otherwise
  // they would miss the very first ticket, which is the one they were for.
  const [pendingMessages, setPendingMessages] = useState([]);

  // Upcoming bookings for this table
  const [upcomingBookings, setUpcomingBookings] = useState([]);

  // Fetch order and payment data
  const fetchOrderData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [menuRes, modifiersRes] = await Promise.all([menuApi.getAll(), modifiersApi.getAll()]);
      setMenuItems(menuRes.data);
      setGlobalModifiers(modifiersRes.data);

      if (orderId) {
        const orderRes = await ordersApi.getById(orderId);
        setOrder(orderRes.data);
        setCart(adoptLines(orderRes.data.items));
        // A bar order has no table. Asking for table `null` was a 404, which threw past the
        // payments below and put up "Failed to load data" over every past bar order.
        if (orderRes.data.table_id) {
          const tableRes = await tablesApi.getById(orderRes.data.table_id);
          setTable(tableRes.data);
        }
        try {
          const paymentsRes = await paymentsApi.getOrderPayments(orderId);
          setOrderPayments(paymentsRes.data.payments || []);
          // `?? ` not `||`: a fully-paid order's remaining_balance is legitimately 0, which
          // `||` would have treated as falsy and fallen through to the full order total.
          setRemainingBalance(paymentsRes.data.remaining_balance ?? balanceOf(orderRes.data));
        } catch (e) {
          setRemainingBalance(balanceOf(orderRes.data));
        }
      } else if (tableId) {
        const tableRes = await tablesApi.getById(tableId);
        setTable(tableRes.data);
        if (tableRes.data.current_order_id) {
          const orderRes = await ordersApi.getById(tableRes.data.current_order_id);
          setOrder(orderRes.data);
          setCart(adoptLines(orderRes.data.items));
          try {
            const paymentsRes = await paymentsApi.getOrderPayments(tableRes.data.current_order_id);
            setOrderPayments(paymentsRes.data.payments || []);
            setRemainingBalance(paymentsRes.data.remaining_balance ?? balanceOf(orderRes.data));
          } catch (e) {
            setRemainingBalance(balanceOf(orderRes.data));
          }
        }
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tableId, orderId]);

  // Fetch upcoming bookings for this table
  useEffect(() => {
    const fetchUpcomingBookings = async () => {
      if (!tableId && !table?.id) return;
      
      try {
        // Booking times are the venue's wall clock ("19:30"), and the till is in the venue,
        // so compare against the till's own date and time. This converted the till's clock to
        // UTC first, which in BST put "now" an hour behind and kept a booking showing as
        // upcoming for an hour after its time; the date was UTC's too, which is yesterday's
        // between midnight and one.
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        const res = await reservationsApi.getByDate(today);
        const tblId = tableId || table?.id;
        const currentTime = now.getHours() * 100 + now.getMinutes();
        
        const upcoming = (res.data || [])
          .filter(r => 
            r.table_id === tblId && 
            r.status === 'confirmed' &&
            parseInt(r.time.replace(':', '')) >= currentTime
          )
          .sort((a, b) => a.time.localeCompare(b.time))
          .slice(0, 3);
        
        setUpcomingBookings(upcoming);
      } catch (e) {
        // Silently fail - upcoming bookings is supplementary
      }
    };
    
    fetchUpcomingBookings();
  }, [tableId, table?.id]);

  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  // Where each sent line is, from the order's tickets. Refreshed with the order and every
  // 20s while it's open — a chef bumping a ticket shows on the pad without a reload.
  const orderIdRef = useRef(null);
  orderIdRef.current = order?.id || null;
  const refreshTickets = useCallback(async () => {
    const id = orderIdRef.current;
    if (!id) { setTickets([]); return; }
    try {
      const res = await kitchenApi.ticketsForOrder(id);
      setTickets(res.data || []);
    } catch { /* the pad still works without progress */ }
  }, []);
  useEffect(() => { refreshTickets(); }, [order?.id, order?.items?.length, refreshTickets]);

  useEffect(() => {
    if (!order?.id) return undefined;
    let cancelled = false;
    promotionsApi.live()
      .then((res) => { if (!cancelled) setTapOffers((res.data?.promotions || []).filter((p) => p.trigger_mode === 'tap')); })
      .catch(() => { if (!cancelled) setTapOffers([]); });
    return () => { cancelled = true; };
  }, [order?.id]);
  useEffect(() => {
    const t = setInterval(refreshTickets, 20000);
    return () => clearInterval(t);
  }, [refreshTickets]);

  // ---- Draft cart: survive a lock (manual or idle) ----
  // The draft key is the order once it exists, else the table it will belong to.
  const draftKey = orderId || tableId;
  const draftRestored = useRef(false);

  // Restore once, after the server load settles: if a draft holds more items than
  // the order already has, it is unsent work — put it back in the cart.
  useEffect(() => {
    if (loading || draftRestored.current) return;
    draftRestored.current = true;
    const draft = loadOrderDraft(draftKey);
    const serverCount = order?.items?.length || 0;
    if (draft && draft.length > serverCount) {
      setCart(draft);
      const extra = draft.length - serverCount;
      toast.info(`Restored ${extra} unsent item${extra === 1 ? '' : 's'} for this table`);
    }
  }, [loading, order, draftKey]);

  // Mirror unsent items as they change; clear once the cart matches the server.
  useEffect(() => {
    if (loading) return;
    const serverCount = order?.items?.length || 0;
    if (cart.length > serverCount) saveOrderDraft(draftKey, cart);
    else clearOrderDraft(draftKey);
  }, [cart, order, loading, draftKey]);

  // Calculate time info
  const getTimeInfo = useCallback(() => {
    if (!table?.opened_at || table.status !== 'occupied') return null;
    const openedAt = new Date(table.opened_at);
    const expectedDuration = table.time_limit_minutes || table.expected_duration || 90;
    const expectedEnd = new Date(openedAt.getTime() + expectedDuration * 60 * 1000);
    const now = new Date();
    const diffMs = expectedEnd - now;
    const diffMins = Math.floor(diffMs / 60000);
    
    // Format seated time in 24hr format
    const seatedTime = openedAt.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    
    if (diffMins > 0) {
      return { text: `${duration(diffMins)} left`, isOvertime: false, minutes: diffMins, expectedDuration, seatedTime };
    } else {
      // Whole minutes over, floored as the ⋯ menu and the floor read it (`turnStatus`).
      const over = Math.floor(-diffMs / 60000);
      return { text: `${duration(over)} over`, isOvertime: true, minutes: diffMins, expectedDuration, seatedTime };
    }
  }, [table]);

  const timeInfo = getTimeInfo();

  // Check for outstanding balance
  const hasOutstandingBalance = useCallback(() => {
    if (order && order.items && order.items.length > 0) {
      const orderTotal = order.total || 0;
      if (orderTotal > 0) {
        const totalPaid = orderPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
        if (totalPaid < orderTotal) return true;
      }
    }
    return false;
  }, [order, orderPayments]);

  // Calculations
  const subtotal = cart.reduce((sum, item) => sum + (item.subtotal || (item.price * item.quantity) || 0), 0);
  // The venue's rate, not 20% written in (Chris's review H3); a saved order shows the tax it
  // was charged.
  const vatRate = useVatRate();
  const vatAmount = vatInside(subtotal, vatRate);
  const total = subtotal + tipAmount;

  // Currency formatter
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
  };

  // === HANDLERS ===
  
  // Status change handler with validation
  const handleStatusChange = async (newStatus) => {
    if (newStatus === table?.status) return;
    
    if (table?.status === 'occupied' && (newStatus === 'available' || newStatus === 'cleaning')) {
      if (hasOutstandingBalance()) {
        setPendingStatus(newStatus);
        setStatusChangeDialogOpen(true);
        return;
      }
    }
    
    try {
      await tablesApi.updateStatus(table.id, newStatus);
      setTable({ ...table, status: newStatus });
      toast.success(`Table marked as ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`);
      if (newStatus !== 'occupied') goHome();
    } catch (err) {
      toast.error('Failed to update table status');
    }
  };

  // Stage change handler
  const handleStageChange = async (newStage) => {
    try {
      await tablesApi.updateStage(table.id, newStage);
      setTable({ ...table, dining_stage: newStage });
      toast.success('Stage updated');
    } catch (err) {
      toast.error('Failed to update stage');
    }
  };

  // Manager override — clear an unpaid bill and free the table. The dialog collects
  // the manager + PIN; the cancel endpoint verifies it (a made order is a void).
  // Anything thrown here surfaces in the dialog, so don't swallow it.
  const handleManagerOverride = async ({ managerId, pin }) => {
    const creds = { manager_id: managerId, manager_pin: pin };
    if (order?.id) await ordersApi.updateStatus(order.id, 'cancelled', creds);
    clearOrderDraft(draftKey);
    // Always set to cleaning after manager override
    await tablesApi.updateStatus(table.id, 'cleaning');
    setTable({ ...table, status: 'cleaning' });
    toast.success('Bill cleared by manager. Table marked as cleaning');
    setPendingStatus(null);
    goHome();
  };

  // Time management handler
  const handleAddTime = async () => {
    try {
      const { data } = await tablesApi.addTime(table.id, additionalTime);
      setTable({ ...table, time_limit_minutes: data.time_limit_minutes });
      toast.success(timeInfo?.isOvertime ? `${additionalTime} minutes from now` : `Added ${additionalTime} minutes`);
      setEditTimeOpen(false);
    } catch (err) {
      console.error('Failed to update time:', err);
      toast.error('Failed to update time');
    }
  };

  // Covers update handler
  const handleUpdateCovers = async () => {
    if (!table?.id) return;
    try {
      await tablesApi.updateDetails(table.id, { covers: newCovers });
      setTable({ ...table, covers: newCovers });
      setEditCoversDialogOpen(false);
      toast.success('Covers updated');
    } catch (err) {
      toast.error('Failed to update covers');
    }
  };

  // Menu item click handler
  const handleMenuItemClick = (item) => {
    // Off right now (hours plan). The server is the authority and refuses it on send; this
    // is so a server finds out on the tap rather than after building a round, and finds
    // out *when it is back* rather than that it is unavailable.
    if (item.available_now === false) {
      toast.error(item.hours ? `${item.name} is on ${item.hours}` : `${item.name} is not on right now`);
      return;
    }
    // "Always ask" (modifiers plan), or a choice that changes the price — a pint or a half
    // set up in the back office — the sheet on every tap (lib/tapChoice).
    if (asksOnTap(item)) {
      openSheet(item);
      return;
    }
    // Check for size options with MORE than 1 option (need to choose)
    if (item.has_sizes && item.size_options?.length > 1) {
      setItemForSize(item);
      setSizeSelectOpen(true);
    } 
    // Single size option - add directly with that size
    else if (item.has_sizes && item.size_options?.length === 1) {
      addToCartWithSize(item, item.size_options[0]);
    }
    // Tap to add (FOH plan round two, F). Modifiers are optional on every item today, so
    // a tap puts the dish straight on the order; the pencil on the line adds sauces,
    // notes or an allergy afterwards. Only a size — a genuine choice — opens a dialog.
    else {
      addToCart(item);
    }
  };

  // Add item with size
  const addToCartWithSize = (item, sizeOption) => {
    const finalPrice = sizeOption.price !== undefined ? sizeOption.price : item.price + (sizeOption.price_modifier || 0);
    const itemName = `${item.name} (${sizeOption.name})`;
    
    setCart([...cart, {
      menu_item_id: item.id,
      name: itemName,
      price: finalPrice,
      quantity: 1,
      modifiers: [],
      notes: '',
      subtotal: finalPrice,
      size: sizeOption.name,
      category: item.category,
    }]);
    setLastAdded(cart.length);
    setSizeSelectOpen(false);
    setItemForSize(null);
  };

  // Add item with modifiers. `modifierTotal` is the priced sum of the selected
  // modifiers (from the same global-modifier list the server bills against) so the
  // cart shows the price the order will actually settle at, not the bare item price.
  const addToCartWithModifiers = (item, modifiers, quantity, modifierTotal = 0) => {
    const modifierText = modifiers.length > 0 ? ` (${modifiers.join(', ')})` : '';
    const itemName = `${item.name}${modifierText}`;
    const unitPrice = item.price + modifierTotal;

    setCart([...cart, {
      menu_item_id: item.id,
      name: itemName,
      price: unitPrice,
      quantity: quantity,
      modifiers: modifiers,
      notes: '',
      subtotal: unitPrice * quantity,
      category: item.category,
    }]);
    setLastAdded(cart.length);
    setModifierSelectOpen(false);
    setItemForModifier(null);
  };

  // Add to cart
  const addToCart = (item) => {
    // Never merge into a comped or price-overridden line — the server only ever
    // authorises a comp/override for the quantity it was granted on, so a merge here
    // would show units as free/overridden in the cart that the bill won't honour
    // (item 9 blocker 2). A fresh tap starts a new line instead.
    const existingIndex = cart.findIndex(
      (c) => c.menu_item_id === item.id && (c.modifiers?.length === 0 || !c.modifiers) && !c.size
        && !c.comp && c.price_override == null && !c.line_id   // a sent line is the kitchen's; a re-order is a new line
    );

    if (existingIndex >= 0) {
      setCart(changeUnsent(cart, existingIndex, 1));
      setLastAdded(existingIndex);
    } else {
      setCart([...cart, {
        menu_item_id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        modifiers: [],
        notes: '',
        subtotal: item.price,
        category: item.category,
      }]);
      setLastAdded(cart.length);
    }
  };

  // A sent line is the kitchen's, so + and − never edit one in place (lib/sentLines.js has
  // the long version). + is another of the same dish, on the next send; − is a void, which
  // goes now. Editing it in place used to change the screen and nothing else: no ticket, no
  // bill, no manager — a binned burger still cooked and still charged.
  const addAnotherOf = (line) => {
    const menuItem = menuItems.find((m) => m.id === line.menu_item_id) || null;
    const another = anotherOf(line, menuItem);
    // A second tap is one more of the line the first tap made.
    const waiting = cart.findIndex((c) => !c.line_id && sameDish(c, another));
    if (waiting >= 0) {
      setCart(changeUnsent(cart, waiting, 1));
      setLastAdded(waiting);
      return;
    }
    setCart([...cart, another]);
    setLastAdded(cart.length);
  };

  // Ask for the void. Nothing on the pad changes until the server has it — a void that is
  // cancelled, refused or fails leaves the screen telling the truth.
  const startVoid = (index, count) => {
    const line = cart[index];
    if (!line?.line_id || !order?.id) return;
    const n = Math.min(count, Number(line.quantity) || 1);
    setPendingVoid({
      items: voidLines(order.items, line.line_id, n).map(toPayloadLine),
      label: `${n}\u00d7 ${line.name}`,
      // Where it went, for the words: a pint is at the bar, not in the kitchen.
      where: courseOf(line) === 'drinks' ? 'the bar' : 'the kitchen',
    });
    if (hasPermission('epos.void')) setVoidConfirmOpen(true);
    else setVoidPromptOpen(true);
  };

  // Send the void. `creds` is a manager's id and PIN when the operator lacks `epos.void`.
  const submitVoid = async (creds = {}) => {
    const res = await ordersApi.updateItems(order.id, { items: pendingVoid.items, ...creds });
    setOrder(res.data);
    // The server's lines, then anything still being built for the next send.
    setCart((prev) => [...adoptLines(res.data.items), ...prev.filter((l) => !l.line_id)]);
    if (res.data.total != null) setRemainingBalance(balanceOf(res.data));
    toast.success(`Voided ${pendingVoid.label}. The kitchen has been told.`)
    setPendingVoid(null);
  };

  const updateQuantity = (index, delta) => {
    const line = cart[index];
    if (!line) return;
    if (line.line_id) {
      if (delta > 0) addAnotherOf(line);
      else startVoid(index, -delta);
      return;
    }
    setCart(changeUnsent(cart, index, delta));
  };

  const removeFromCart = (index) => {
    const line = cart[index];
    if (!line) return;
    if (line.line_id) {
      startVoid(index, Number(line.quantity) || 1);
      return;
    }
    setCart(cart.filter((_, i) => i !== index));
  };

  // Open item edit dialog
  //
  // Every line the menu still knows goes to the sheet — the same surface the dish was
  // ordered through. It used to be split: a dish with modifier groups reopened in the
  // sheet, one without fell back to a second dialog with its own allergy and notes boxes.
  // Two places to write the same sentence, laid out differently, is how a server learns
  // that one of them is the real one. The fallback survives only for a line whose menu item
  // has been delisted, where there is nothing for the sheet to draw.
  const openItemEditDialog = (index) => {
    const item = cart[index];
    const menuItem = menuItems.find((m) => m.id === item.menu_item_id);
    if (menuItem) {
      openSheet(menuItem, sheetInitialFor(item), index);
      return;
    }
    setEditingCartIndex(index);
    setItemModifiers(item.modifiers || []);
    setItemNotes(item.notes || '');
    setItemAllergyNotes(item.allergy_notes || '');
    setCustomModifier('');
    setItemEditDialogOpen(true);
  };

  // Save item modifications
  const saveItemModifications = () => {
    if (editingCartIndex !== null) {
      const newCart = [...cart];
      newCart[editingCartIndex] = {
        ...newCart[editingCartIndex],
        modifiers: itemModifiers,
        notes: itemNotes,
        allergy_notes: itemAllergyNotes,
      };
      setCart(newCart);
      toast.success('Item updated with modifications');
    }
    setItemEditDialogOpen(false);
    setEditingCartIndex(null);
    setItemModifiers([]);
    setItemNotes('');
    setItemAllergyNotes('');
  };

  // ----- Item 9: comps, discounts, price overrides -----

  const applyOrderMoney = (res) => {
    setOrder(res.data);
    // Adopt the server's lines (fresh prices, comp/override state) but keep any cart
    // lines added since the last send — they have no `line_id` yet, so a wholesale
    // replace would silently drop items mid-build (should-fix 3).
    setCart((prev) => [...adoptLines(res.data.items), ...prev.filter((it) => !it.line_id)]);
    if (res.data.total != null) setRemainingBalance(balanceOf(res.data));
  };

  // Opens the apply dialog. `mode` is 'discount' | 'comp' | 'price'; `line` is the
  // cart line for comp/price.
  const openManagerAction = (mode, line = null) => {
    const permMap = { discount: 'epos.discount', comp: 'epos.comp', price: 'epos.manager_override' };
    setManagerAction({
      mode,
      needsAuth: !hasPermission(permMap[mode]),
      lineId: line?.line_id || null,
      lineName: line?.name || null,
      currentPrice: line?.price,
    });
  };

  const submitManagerAction = async (payload) => {
    const { mode, lineId } = managerAction;
    let res;
    if (mode === 'discount') res = await ordersApi.applyDiscount(order.id, payload);
    else if (mode === 'comp') res = await ordersApi.compLine(order.id, lineId, payload);
    else res = await ordersApi.overrideLinePrice(order.id, lineId, payload);
    applyOrderMoney(res);
    setManagerAction(null);
    toast.success(
      mode === 'discount' ? 'Discount applied'
        : mode === 'comp' ? 'Item comped' : 'Price overridden',
    );
  };

  // A message for the table. No manager: it moves no money, and fetching one to say
  // "no onions" is how a kitchen ends up being told nothing.
  const submitMessage = async (payload) => {
    if (!order?.id) {
      setPendingMessages((prev) => [...prev, { ...payload, id: `pending-${Date.now()}` }]);
      toast.success(payload.kind === 'house' ? 'Noted for the floor' : 'It will go with the order');
      return;
    }
    const res = await ordersApi.addMessage(order.id, payload);
    setOrder(res.data);
    toast.success(payload.kind === 'house' ? 'Noted for the floor' : 'The kitchen has been told');
  };

  const removeMessage = async (message) => {
    if (!order?.id) {
      setPendingMessages((prev) => prev.filter((m) => m.id !== message.id));
      return;
    }
    try {
      const res = await ordersApi.removeMessage(order.id, message.id);
      setOrder(res.data);
      toast.success('Message removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove it');
    }
  };

  const submitOffer = async (payload) => {
    const res = await ordersApi.applyOffer(order.id, payload);
    applyOrderMoney(res);
    toast.success(`${res.data?.discount_promotion_name || 'Offer'} applied`);
  };

  // Undo (remove discount / uncomp / clear override). Direct when the operator
  // holds the permission, otherwise a manager PIN prompt.
  const runUndo = async (kind, lineId, creds = null) => {
    let res;
    if (kind === 'offer') res = await ordersApi.removeOffer(order.id);
    else if (kind === 'discount') res = await ordersApi.removeDiscount(order.id, creds);
    else if (kind === 'comp') res = await ordersApi.uncompLine(order.id, lineId, creds);
    else res = await ordersApi.clearLinePrice(order.id, lineId, creds);
    applyOrderMoney(res);
    toast.success('Adjustment removed');
  };

  const handleUndo = (kind, line = null) => {
    const permMap = { discount: 'epos.discount', comp: 'epos.comp', price: 'epos.manager_override' };
    if (kind === 'offer' || hasPermission(permMap[kind])) {
      runUndo(kind, line?.line_id).catch((e) => {
        toast.error(e?.response?.data?.detail || 'Could not remove the adjustment');
      });
    } else {
      setUndoAction({ kind, lineId: line?.line_id || null });
    }
  };

  // Every dish on the menu by id, for allergens: a meal's parts are menu items too.
  const tableAllergens = useMemo(() => table?.allergens || [], [table?.allergens]);
  // Where this server is, for the lock screen ("Sarah was on T1 · 2 items not sent yet").
  const unsentCount = cart.filter((l) => !l.line_id).reduce((n, l) => n + (Number(l.quantity) || 1), 0);
  useEffect(() => {
    if (!table?.name) return undefined;
    setWhere({ path: window.location.pathname, label: table.name, unsent: unsentCount });
    return () => clearWhere();
  }, [table?.name, unsentCount]);
  const menuById = useMemo(() => Object.fromEntries(menuItems.map((m) => [m.id, m])), [menuItems]);

  // The occasion, set or changed after seating: the seat sheet is not the only time a server
  // hears "it's her birthday".
  const saveOccasion = async () => {
    if (!table?.id || !occasionEdit) return;
    try {
      const res = await tablesApi.updateDetails(table.id, {
        occasion: occasionEdit.occasion,
        occasion_notes: occasionEdit.occasion === 'other' ? occasionEdit.notes.trim() : '',
      });
      setTable(res.data);
      setOccasionEdit(null);
      toast.success(occasionEdit.occasion ? 'Occasion saved' : 'Occasion taken off');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the occasion");
    }
  };

  const saveAllergy = async () => {
    if (!table?.id || !allergyEdit) return;
    try {
      const res = await tablesApi.updateDetails(table.id, {
        allergens: allergyEdit.allergens,
        allergy_notes: allergyEdit.notes,
        has_allergies: allergyEdit.allergens.length > 0 || !!allergyEdit.notes.trim(),
      });
      setTable(res.data);
      setAllergyEdit(null);
      toast.success('Allergies updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the allergies");
      // 409: the party has left this table (moved, from another till). Show it as it is now.
      if (err?.response?.status === 409) { setAllergyEdit(null); fetchOrderData(false); }
    }
  };

  // Clear the table and go back to the floor: the pad's verb once a bill is paid, and the
  // button on the payment's "Paid" screen.
  const clearTable = async () => {
    try {
      await tablesApi.close(table.id);
      clearOrderDraft(draftKey);
      toast.success(`${table.name} cleared`);
      goHome();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not clear the table');
    }
  };

  // Create/Update order. `linesOverride` is the cart as the allergy check left it (notes
  // written, or dishes taken off): state hasn't caught up by the time we send.
  const handleCreateOrder = async (linesOverride, { allergyChecked = false } = {}) => {
    const lines = Array.isArray(linesOverride) ? linesOverride : cart;
    if (lines.length === 0) {
      toast.error('Add items to the order first');
      return;
    }
    // A dish this table can't have stops here once, so the server can check with the guest.
    if (!allergyChecked) {
      const hits = clashingLines(lines, table?.allergens, menuById);
      if (hits.length) {
        setClashHits(hits);
        return;
      }
    }

    setProcessing(true);
    try {
      if (order) {
        // Update existing order with new items. `line_id` is passed straight back
        // so the server can match each line to its stored comp / price-override
        // state (item 9) — a line without one is treated as newly added.
        const updatedItems = lines.map((item) => ({
          ...(item.line_id ? { line_id: item.line_id } : {}),
          menu_item_id: item.menu_item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          modifiers: item.modifiers || [],
          notes: item.notes || '',
          // What this dish must not contain. It used to stop at the browser, so a server
          // typed "nut allergy" and the kitchen never saw it.
          allergy_notes: item.allergy_notes || '',
          // A meal's choices. The server re-resolves them against the item's own groups.
          components: (item.components || []).map((c) => ({
            group: c.group, menu_item_id: c.menu_item_id, quantity: c.quantity || 1,
          })),
          subtotal: item.price * item.quantity,
          size: item.size || null,
          category: item.category,
        }));

        // Removing a line that's already been sent to the kitchen is a void.
        // A server without `epos.void` has to get a manager to approve it; the
        // backend enforces this too, this just prompts before the round-trip.
        const alreadySent = order.status && order.status !== 'pending';
        if (alreadySent
            && reducesExistingItems(order.items, updatedItems)
            && !hasPermission('epos.void')) {
          setPendingVoid({ items: updatedItems, label: 'the removed items' });
          setVoidPromptOpen(true);
          setProcessing(false);
          return;
        }

        // Nothing new, only a message changed on a line already with the kitchen — the
        // board updates and a slip prints; saying "sent" would claim food that isn't new.
        const sentLineIds = new Set((order.items || []).filter((l) => l.line_id).map((l) => l.line_id));
        const onlyAmended = updatedItems.every((l) => l.line_id)
          && updatedItems.filter((l) => sentLineIds.has(l.line_id)).length === sentLineIds.size;
        const res = await ordersApi.updateItems(order.id, { items: updatedItems });
        setOrder(res.data);
        setCart(adoptLines(res.data.items));   // adopt server line_ids + comp/override state
        if (res.data.total) setRemainingBalance(balanceOf(res.data));
        setLastAdded(null);
        toast.success(onlyAmended ? 'The kitchen has been told' : 'Sent to the kitchen');
      } else {
        const orderData = {
          table_id: tableId,
          items: lines.map((item) => ({
            menu_item_id: item.menu_item_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            modifiers: item.modifiers || [],
            notes: item.notes || '',
            allergy_notes: item.allergy_notes || '',
            components: (item.components || []).map((c) => ({
              group: c.group, menu_item_id: c.menu_item_id, quantity: c.quantity || 1,
            })),
            category: item.category,
            size: item.size || null,
          })),
          messages: pendingMessages.map(({ kind, text }) => ({ kind, text })),
        };
        const res = await ordersApi.create(orderData);
        setOrder(res.data);
        setPendingMessages([]);
        setCart(adoptLines(res.data.items));   // adopt server line_ids
        if (res.data.total) setRemainingBalance(balanceOf(res.data));
        setLastAdded(null);
        toast.success('Sent to the kitchen');
      }
    } catch (err) {
      console.error('Order error:', err);
      // The server says why (a typed extra over the limit needs a manager; priced free text
      // is off on this till) — show that rather than a bare failure.
      const detail = err.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to save order');
    } finally {
      setProcessing(false);
    }
  };

  // Payment handlers
  //
  // There is no fake/simulated success path. The server is the only thing that can
  // confirm a payment (issue #13); if the request fails, the operator is told and the
  // dialog goes back to waiting. `paymentKeyRef` carries one idempotency key per
  // attempt so a retry of the same confirm can't double-record — it's cleared whenever
  // the flow returns to 'waiting' for a fresh payment.
  const payingRef = useRef(false);

  const recordPayment = async (amount, tendered) => {
    if (payingRef.current) return;          // ignore a double-tap while a request is in flight
    if (!order?.id) {
      toast.error('Send the order to the kitchen first, then take payment.');
      setCheckoutOpen(false);
      return;
    }
    if (!(amount > 0)) {
      toast.error('Enter an amount greater than zero.');
      return;
    }
    payingRef.current = true;
    setPaymentStep('processing');
    try {
      // Generated inside the try: crypto.randomUUID() is undefined outside a secure context
      // (plain HTTP, which is how the kiosk is normally reached on the LAN), and throwing
      // before this point would leave payingRef stuck true and the dialog stuck on
      // "Recording payment…" with no way to retry.
      if (!paymentKeyRef.current) paymentKeyRef.current = randomIdempotencyKey();
      const response = await paymentsApi.addPartialPayment(order.id, {
        order_id: order.id,
        amount,
        method: paymentMethod,
        tip_amount: tipAmount || 0,
        ...(paymentMethod === 'cash' && tendered != null ? { tendered } : {}),
        idempotency_key: paymentKeyRef.current,
      });
      setOrderPayments(response.data.payments);
      setRemainingBalance(response.data.remaining_balance);
      if (response.data.is_fully_paid) setOrder({ ...order, status: 'paid' });
      paymentKeyRef.current = null;
      setPaymentStep('success');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Payment could not be recorded. Check the card machine and try again.');
      setPaymentStep('waiting');
    } finally {
      payingRef.current = false;
    }
  };

  const handlePaymentSuccess = (tendered) => recordPayment(remainingBalance > 0 ? remainingBalance : total, tendered);
  const handlePartialPaymentSuccess = (paymentAmount, tendered) => recordPayment(paymentAmount, tendered);

  // Loading state
  if (loading) {
    return (
      <div className="flex gap-6 h-[calc(100vh-8rem)]">
        <div className="flex-1">
          <Skeleton className="h-10 w-64 mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-40 rounded-lg" />)}
          </div>
        </div>
        <Skeleton className="w-96 h-full rounded-lg" />
      </div>
    );
  }

  const settled = Boolean(orderId && order && SETTLED.includes(order.status));
  // On a till's screen (lg and up) the page is exactly the screen's height: the header and the
  // tabs stay put, and only the dishes and the order's lines scroll (4:3 tills, Oct 2026).
  return (
    <div className="flex flex-col gap-4 min-h-[calc(100vh-8rem)] lg:flex-row lg:h-[calc(100vh-7rem)] lg:min-h-0" data-testid="table-order-page">
      {/* Menu Section. `min-w-0`: a flex item is otherwise as wide as its widest content, and
          the section tabs (which scroll) made the column ~940px, pushing the order and Bill
          off a 1024x768 till. */}
      <div className="flex flex-1 min-w-0 flex-col gap-3 lg:min-h-0">
        <TableOrderHeader
          table={table}
          // A past order opened from Orders is named by its number, as the Orders list names
          // it, not "New Order"; where it was and when go underneath.
          title={orderId && order ? `Order ${orderRef(order)}` : undefined}
          subtitle={orderId && order ? [order.table_name || (order.type === 'bar' ? 'Bar' : null),
            order.created_at && new Date(order.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })]
            .filter(Boolean).join(' · ') : undefined}
          timeInfo={timeInfo}
          upcomingBookings={upcomingBookings}
          onBack={goHome}
          from={from}
          onStatusChange={handleStatusChange}
          onStageChange={handleStageChange}
          onMove={table?.id ? () => setMoveOpen(true) : undefined}
          onEditCovers={() => {
            setNewCovers(table?.covers || 1);
            setEditCoversDialogOpen(true);
          }}
          onEditTime={() => setEditTimeOpen(true)}
          onEditAllergy={table?.status === 'occupied' ? () => setAllergyEdit({
            allergens: table?.allergens || [],
            notes: table?.has_allergies ? (table?.allergy_notes || '') : '',
          }) : undefined}
          onEditOccasion={table?.status === 'occupied' ? () => setOccasionEdit({
            occasion: table?.occasion && table.occasion !== 'none' ? table.occasion : '',
            notes: table?.occasion_notes || '',
          }) : undefined}
        />

        {settled ? (
          <Notice tone="info" word="Record" title={`This order is ${String(order.status).replace('_', ' ')}`}>
            Nothing on it can change. A manager can refund it from the pad.
          </Notice>
        ) : (
        <MenuSection
          menuItems={menuItems}
          onItemClick={handleMenuItemClick}
          onItemInfo={(item) => openSheet(item)}
          formatCurrency={formatCurrency}
          tableAllergens={tableAllergens}
          fill
        />
        )}
      </div>

      {/* The order — one list, one verb */}
      <OrderPanel
        fill
        cart={cart}
        order={order}
        tickets={tickets}
        lastAdded={lastAdded}
        onCloseTable={clearTable}
        orderPayments={orderPayments}
        remainingBalance={remainingBalance}
        subtotal={subtotal}
        vatAmount={vatAmount}
        // The till's prices include VAT: an order's own `tax` is VAT added on top, which the
        // till never charges, so it reads £0.00. "Includes VAT" is the share of the bill that's
        // VAT, as the pad shows before the order is sent.
        orderTax={order?.tax ?? (order?.total ? vatInside(order.total, vatRate) : 0)}
        processing={processing}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeFromCart}
        onEditItem={openItemEditDialog}
        onCreateOrder={handleCreateOrder}
        onCheckout={openCheckout}
        formatCurrency={formatCurrency}
        discountAmount={order?.discount_amount || 0}
        discountLabel={order?.discount_reason === 'promotion'
          ? `${order?.discount_promotion_name || 'Offer'}${order?.discount_type === 'percent' ? ` · ${order?.discount_input}%` : ''}`
          : order?.discount_type === 'percent'
            ? `Discount (${order?.discount_input}%)`
            : 'Discount'}
        orderTotal={order?.total}
        readOnly={settled}
        onRefund={settled ? () => setRefundOpen(true) : undefined}
        canAdjust={Boolean(order?.id)}
        pendingMessages={pendingMessages}
        onManagerAction={openManagerAction}
        onUndoAdjustment={handleUndo}
        onMessage={() => setMessageOpen(true)}
        onRemoveMessage={removeMessage}
        offersAvailable={tapOffers.length}
        onOffers={() => setOffersOpen(true)}
        isOffer={order?.discount_reason === 'promotion'}
      />

      {/* Dialogs */}
      <SizeSelectDialog
        open={sizeSelectOpen}
        onOpenChange={setSizeSelectOpen}
        item={itemForSize}
        onSelectSize={addToCartWithSize}
        formatCurrency={formatCurrency}
      />

      <ModifierSelectDialog
        open={modifierSelectOpen}
        onOpenChange={setModifierSelectOpen}
        item={itemForModifier}
        onConfirm={addToCartWithModifiers}
        formatCurrency={formatCurrency}
        globalModifiers={globalModifiers}
      />

      <ModifierSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        item={sheetItem}
        initial={sheetInitial}
        confirmLabel={sheetEditIndex != null ? 'Update line' : 'Add to order'}
        formatCurrency={formatCurrency}
        onConfirm={(item, mods, modifierTotal, unit, message = {}) => {
          setCart((prev) => {
            if (sheetEditIndex != null && prev[sheetEditIndex]) {
              const next = [...prev];
              const line = {
                ...next[sheetEditIndex], modifiers: mods, price: unit,
                ...(message.size ? { size: message.size, name: sizedName(item.name, message.size) } : {}),
                notes: message.notes ?? next[sheetEditIndex].notes ?? '',
                allergy_notes: message.allergy_notes ?? next[sheetEditIndex].allergy_notes ?? '',
                ...(message.components ? { components: message.components, is_meal: true } : {}),
              };
              line.subtotal = Math.round(unit * line.quantity * 100) / 100;
              next[sheetEditIndex] = line;
              return next;
            }
            const line = {
              menu_item_id: item.id, name: sizedName(item.name, message.size), price: unit, quantity: 1,
              ...(message.size ? { size: message.size } : {}),
              modifiers: mods, notes: message.notes || '',
              allergy_notes: message.allergy_notes || '',
              subtotal: unit, category: item.category,
              ...(message.components ? { components: message.components, is_meal: true } : {}),
            };
            // The same again, with the same choices, is one more on its line (lib/sentLines).
            return addOrMore(prev, line);
          });
          const words = ticketWords(mods);
          toast.success(`${sheetEditIndex != null ? 'Updated' : 'Added'} ${sizedName(item.name, message.size)}${words.length ? ` · ${words.join(', ')}` : ''}`);
        }}
      />

      <ItemDetailDialog
        open={itemDetailOpen}
        onOpenChange={setItemDetailOpen}
        item={selectedItem}
        onAddToOrder={(item, options) => {
          if (options?.modifiers?.length > 0) {
            // Add directly with modifiers (skip modifier popup)
            const selectedSize = options.size || (item.has_sizes && item.size_options?.length === 1 ? item.size_options[0].name : null);
            addToCart({
              ...item,
              selectedModifiers: options.modifiers,
              selectedSize,
            });
          } else if (options?.size) {
            addToCart({ ...item, selectedSize: options.size });
          } else {
            handleMenuItemClick(item);
          }
        }}
        formatCurrency={formatCurrency}
      />

      <EditCoversDialog
        open={editCoversDialogOpen}
        onOpenChange={setEditCoversDialogOpen}
        covers={newCovers}
        maxCapacity={table?.capacity || 20}
        onCoversChange={setNewCovers}
        onConfirm={handleUpdateCovers}
      />

      {table && (
        <MoveTableDialog
          open={moveOpen}
          onOpenChange={setMoveOpen}
          table={table}
          orderTotal={order?.total}
          // The party is at another table now: this page follows them there (a new page: the
          // route keys it by table). The dialog has moved the table's draft; an order opened
          // from Orders parks its unsent items under the order, so those go too.
          onMoved={(to) => {
            if (orderId) moveOrderDraft(orderId, to.id);
            navigate(`/tables/${to.id}/order`, { replace: true });
          }}
        />
      )}
      <AllergyClashDialog
        open={!!clashHits}
        onOpenChange={(v) => { if (!v) setClashHits(null); }}
        hits={clashHits || []}
        tableName={table?.name || 'This table'}
        tableAllergens={tableAllergens}
        allergyNotes={table?.has_allergies ? table?.allergy_notes : ''}
        onSend={(notes) => {
          const next = withAllergyNotes(cart, notes);
          setCart(next);
          setClashHits(null);
          handleCreateOrder(next, { allergyChecked: true });
        }}
        onRemove={(indexes) => {
          setCart(withoutLines(cart, indexes));
          setClashHits(null);
          toast.success(indexes.length === 1 ? 'Taken off the order' : `${indexes.length} dishes taken off the order`);
        }}
      />

      <Dialog open={!!allergyEdit} onOpenChange={(v) => { if (!v) setAllergyEdit(null); }}>
        <DialogContent size="sheet" data-testid="allergy-edit-dialog">
          <DialogHeader>
            <DialogTitle>Allergies at {table?.name}</DialogTitle>
          </DialogHeader>
          {allergyEdit && (
            <AllergyPicker
              allergens={allergyEdit.allergens}
              notes={allergyEdit.notes}
              menu={menuItems}
              onChange={setAllergyEdit}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllergyEdit(null)}>Cancel</Button>
            <Button className="bg-verlo" onClick={saveAllergy} data-testid="allergy-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!occasionEdit} onOpenChange={(v) => { if (!v) setOccasionEdit(null); }}>
        <DialogContent size="form" data-testid="occasion-edit-dialog">
          <DialogHeader>
            <DialogTitle>Occasion at {table?.name}</DialogTitle>
          </DialogHeader>
          {occasionEdit && (
            <OccasionPicker
              occasion={occasionEdit.occasion}
              notes={occasionEdit.notes}
              onChange={setOccasionEdit}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOccasionEdit(null)}>Cancel</Button>
            <Button className="bg-verlo" onClick={saveOccasion} data-testid="occasion-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExtendTimeDialog
        open={editTimeOpen}
        onOpenChange={setEditTimeOpen}
        timeInfo={timeInfo}
        additionalTime={additionalTime}
        onAdditionalTimeChange={setAdditionalTime}
        onConfirm={handleAddTime}
      />

      <ItemEditDialog
        open={itemEditDialogOpen}
        onOpenChange={setItemEditDialogOpen}
        itemName={editingCartIndex !== null ? cart[editingCartIndex]?.name : ''}
        modifiers={itemModifiers}
        notes={itemNotes}
        allergyNotes={itemAllergyNotes}
        customModifier={customModifier}
        onModifiersChange={setItemModifiers}
        onNotesChange={setItemNotes}
        onAllergyNotesChange={setItemAllergyNotes}
        onCustomModifierChange={setCustomModifier}
        onSave={saveItemModifications}
      />

      <StatusChangeDialog
        open={statusChangeDialogOpen}
        onOpenChange={(open) => {
          setStatusChangeDialogOpen(open);
          if (!open) setPendingStatus(null);
        }}
        balance={remainingBalance || order?.total || 0}
        onPayBalance={() => {
          setStatusChangeDialogOpen(false);
          openCheckout();
        }}
        onManagerOverride={() => {
          setStatusChangeDialogOpen(false);
          setManagerOverrideOpen(true);
        }}
        formatCurrency={formatCurrency}
      />

      <ManagerOverrideDialog
        open={managerOverrideOpen}
        onOpenChange={setManagerOverrideOpen}
        balance={remainingBalance || order?.total || 0}
        siteId={order?.site_id || table?.site_id}
        onSubmit={handleManagerOverride}
        confirmLabel="Authorize & Clear Bill"
        formatCurrency={formatCurrency}
      />

      {/* Void step-up — a server trimming an already-sent order (item 9). The
          caller makes the guarded PUT itself and the dialog surfaces any error. */}
      <ManagerOverrideDialog
        open={voidPromptOpen}
        onOpenChange={(v) => {
          setVoidPromptOpen(v);
          if (!v) setPendingVoid(null);
        }}
        siteId={order?.site_id || table?.site_id}
        title="Void authorisation"
        description={pendingVoid ? `Void ${pendingVoid.label}? It has already gone to ${pendingVoid.where || 'the kitchen'}, so it needs a manager's PIN.` : "Removing an item that's already been sent needs a manager's PIN."}
        warning="The removed items will be voided from this order. This action is logged."
        confirmLabel="Authorise void"
        onSubmit={({ managerId, pin }) => submitVoid({ manager_id: managerId, manager_pin: pin })}
        formatCurrency={formatCurrency}
      />

      {/* A manager's void: no PIN, one moment to confirm the dish. */}
      <VoidConfirmDialog
        open={voidConfirmOpen}
        onOpenChange={(v) => { setVoidConfirmOpen(v); if (!v) setPendingVoid(null); }}
        label={pendingVoid?.label}
        where={pendingVoid?.where}
        onConfirm={() => submitVoid()}
      />

      {/* Item 9 — apply a discount / comp / price override. The dialog collects a
          manager PIN itself when the operator doesn't hold the permission. */}
      <ManagerActionDialog
        open={Boolean(managerAction)}
        onOpenChange={(v) => { if (!v) setManagerAction(null); }}
        mode={managerAction?.mode}
        needsAuth={managerAction?.needsAuth}
        siteId={order?.site_id || table?.site_id}
        lineName={managerAction?.lineName}
        currentPrice={managerAction?.currentPrice}
        onSubmit={submitManagerAction}
        formatCurrency={formatCurrency}
      />

      {/* A message for the table — an allergy, an instruction, or something only the floor
          needs (30 Sep 2026). */}
      <MessageDialog
        open={messageOpen}
        onOpenChange={setMessageOpen}
        onSubmit={submitMessage}
        started={Boolean(order?.kitchen_status && order.kitchen_status !== 'pending')}
      />

      {/* Promotions — a bill-level offer a server taps on. */}
      <OffersDialog
        open={offersOpen}
        onOpenChange={setOffersOpen}
        offers={tapOffers}
        canDiscount={hasPermission('epos.discount')}
        siteId={order?.site_id || table?.site_id}
        onSubmit={submitOffer}
      />

      {/* Undo an adjustment when the operator can't self-authorise. */}
      <ManagerOverrideDialog
        open={Boolean(undoAction)}
        onOpenChange={(v) => { if (!v) setUndoAction(null); }}
        siteId={order?.site_id || table?.site_id}
        title="Remove adjustment"
        description="Taking this back off needs a manager's PIN."
        warning="The adjustment will be removed and the change is logged."
        confirmLabel="Authorise"
        onSubmit={async ({ managerId, pin }) => {
          await runUndo(undoAction.kind, undoAction.lineId, { manager_id: managerId, manager_pin: pin });
          setUndoAction(null);
        }}
        formatCurrency={formatCurrency}
      />

      <PaymentDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        paymentStep={paymentStep}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        splitBillMode={splitBillMode}
        splitType={splitType}
        splitPeople={splitPeople}
        customAmount={customAmount}
        orderPayments={orderPayments}
        remainingBalance={remainingBalance}
        total={total}
        tipAmount={tipAmount}
        onTipChange={setTipAmount}
        order={order}
        orderId={order?.id}
        onPaymentStepChange={goToPaymentStep}
        onSplitBillModeChange={setSplitBillMode}
        onSplitTypeChange={setSplitType}
        onSplitPeopleChange={setSplitPeople}
        onCustomAmountChange={setCustomAmount}
        onPaymentSuccess={handlePaymentSuccess}
        onPartialPaymentSuccess={handlePartialPaymentSuccess}
        tableLabel={table?.name}
        onClearTable={table?.id ? () => { setCheckoutOpen(false); setTipAmount(0); clearTable(); } : undefined}
        onViewReceipt={() => {
          // Back to the pad, whose one verb is now "Close table".
          setCheckoutOpen(false);
          setTipAmount(0);
        }}
        formatCurrency={formatCurrency}
      />
      {settled && (
        <RefundDialog
          open={refundOpen}
          onOpenChange={setRefundOpen}
          order={order}
          onRefunded={() => { setRefundOpen(false); fetchOrderData(); }}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

export default TableOrderPage;
